import { useState, useEffect } from 'react';
import { usuariosApi, gastosApi } from '../../utils/api';
import { Users, Plus, Pencil, Delete, X, Check, Mail, Shield, Building2, Eye, EyeOff, Search, AlertCircle, UserCog } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usuariosRes, rolesRes, empresasRes] = await Promise.all([
        usuariosApi.list(),
        usuariosApi.listRolesActivos(),
        usuariosApi.listEmpresas()
      ]);
      setUsuarios(usuariosRes.data.data || []);
      setRoles(rolesRes.data.data || []);
      setEmpresas(empresasRes.data.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (user) => {
    if (!confirm(`¿Está seguro de eliminar al usuario "${user.name}"?`)) return;

    try {
      await usuariosApi.delete(user.id);
      toast.success('Usuario eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar');
    }
  };

  const openNew = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const openEdit = async (user) => {
    try {
      const res = await usuariosApi.get(user.id);
      setEditingUser(res.data.data);
      setShowModal(true);
    } catch (error) {
      toast.error('Error al cargar usuario');
    }
  };

  const handleSave = async (formData) => {
    try {
      if (editingUser) {
        await usuariosApi.update(editingUser.id, formData);
        toast.success('Usuario actualizado');
      } else {
        await usuariosApi.create(formData);
        toast.success('Usuario creado');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end">
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
        >
          <Plus size={18} />
          Nuevo Usuario
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-gray-500">Cargando...</p>
          </div>
        ) : usuarios.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No hay usuarios registrados.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Usuario</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Rol</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Último Acceso</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {usuarios.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Mail size={12} />
                        {user.email}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {user.rol_nombre ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm">
                        <Shield size={12} />
                        {user.rol_nombre}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-sm">Sin rol</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={clsx(
                      'px-2 py-1 rounded text-xs font-medium',
                      user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    )}>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {user.last_login ? new Date(user.last_login).toLocaleString('es-CR') : 'Nunca'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => openEdit(user)}
                        className="p-1.5 hover:bg-blue-50 rounded"
                        title="Editar"
                      >
                        <Pencil className="h-5 w-5 text-blue-500" />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className="p-1.5 hover:bg-red-50 rounded"
                        title="Eliminar"
                      >
                        <Delete className="h-5 w-5 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <UserModal
          user={editingUser}
          roles={roles}
          empresas={empresas}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// User Modal Component
function UserModal({ user, roles, empresas, onClose, onSave }) {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    password: '',
    name: user?.name || '',
    roles: user?.roles?.map(r => r.id) || [],
    is_active: user?.is_active !== false,
    empresas: user?.empresas || []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showProveedoresModal, setShowProveedoresModal] = useState(false);
  const [proveedoresConfig, setProveedoresConfig] = useState({
    grupos: [],
    proveedores: []
  });

  // Check if any selected role has CxP permission
  const selectedRoles = roles.filter(r => formData.roles.includes(r.id));
  const hasCxPPermission = selectedRoles.some(role =>
    role?.permisos?.some(p => p.modulo === 'cxp' || p.modulo === 'gastos')
  );

  // Toggle role selection
  const toggleRole = (roleId) => {
    if (formData.roles.includes(roleId)) {
      setFormData({ ...formData, roles: formData.roles.filter(id => id !== roleId) });
    } else {
      setFormData({ ...formData, roles: [...formData.roles, roleId] });
    }
  };

  // Load existing provider restrictions when editing
  useEffect(() => {
    if (user?.id && formData.empresas.length > 0) {
      loadProveedoresPermitidos();
    }
  }, [user?.id, formData.empresas]);

  const loadProveedoresPermitidos = async () => {
    if (!user?.id || formData.empresas.length === 0) return;

    try {
      const emisorId = formData.empresas[0]?.emisor_id;
      if (!emisorId) return;

      const res = await usuariosApi.getProveedoresPermitidos(user.id, emisorId);
      if (res.data.success) {
        // Extract just the IDs from the response objects
        setProveedoresConfig({
          grupos: (res.data.data?.grupos || []).map(g => g.grupoId),
          proveedores: (res.data.data?.proveedores || []).map(p => p.proveedorId)
        });
      }
    } catch (error) {
      console.error('Error loading provider permissions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...formData };
      if (!data.password && user) {
        delete data.password; // Don't send empty password on edit
      }
      await onSave(data);
    } finally {
      setLoading(false);
    }
  };

  const toggleEmpresa = (empresaId) => {
    const exists = formData.empresas.find(e => e.emisor_id === empresaId);
    if (exists) {
      setFormData({
        ...formData,
        empresas: formData.empresas.filter(e => e.emisor_id !== empresaId)
      });
    } else {
      setFormData({
        ...formData,
        empresas: [...formData.empresas, { emisor_id: empresaId, can_create: true, can_edit: true, can_delete: false }]
      });
    }
  };

  const handleSaveProveedoresConfig = (config) => {
    setProveedoresConfig(config);
    setShowProveedoresModal(false);

    // Save to backend if we have a user ID
    if (user?.id && formData.empresas.length > 0) {
      const emisorId = formData.empresas[0]?.emisor_id;
      if (emisorId) {
        usuariosApi.saveProveedoresPermitidos(user.id, {
          emisorId,
          grupoIds: config.grupos,
          proveedorIds: config.proveedores
        }).then(() => {
          toast.success('Permisos de proveedores guardados');
        }).catch(err => {
          toast.error('Error al guardar permisos de proveedores');
          console.error(err);
        });
      }
    }
  };

  const hasRestrictions = proveedoresConfig.grupos.length > 0 || proveedoresConfig.proveedores.length > 0;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-full max-w-5xl mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
            <h3 className="text-lg font-semibold">{user ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {/* 27/10/2026: los datos del usuario en una fila; abajo Roles y Empresas en dos columnas del mismo alto */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Correo *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña {user ? '(vacía = no cambia)' : '*'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      required={!user}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={formData.is_active ? 'true' : 'false'}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value="true">Activo</option>
                    <option value="false">Inactivo</option>
                  </select>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="min-w-0">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
                  <div className="border rounded-lg grid grid-cols-1 sm:grid-cols-2 max-h-[45vh] overflow-y-auto">
                    {roles.length === 0 ? (
                      <p className="p-3 text-gray-500 text-sm">No hay roles disponibles</p>
                    ) : (
                      roles.map((rol) => {
                        const isSelected = formData.roles.includes(rol.id);
                        return (
                          <label
                            key={rol.id}
                            className="flex items-start gap-3 p-2.5 border-b hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRole(rol.id)}
                              className="h-4 w-4 mt-0.5 text-blue-600 rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">{rol.nombre}</p>
                              {rol.descripcion && (
                                <p className="text-xs text-gray-500">{rol.descripcion}</p>
                              )}
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                  {formData.roles.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">Sin roles asignados</p>
                  )}
                </div>
              </div>

              {/* Columna derecha: empresas */}
              <div className="space-y-4 min-w-0">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Empresas asignadas</label>
                  <div className="border rounded-lg divide-y max-h-[45vh] overflow-y-auto">
                    {empresas.length === 0 ? (
                      <p className="p-3 text-gray-500 text-sm">No hay empresas disponibles</p>
                    ) : (
                      empresas.map((emp) => {
                        const isAssigned = formData.empresas.some(e => e.emisor_id === emp.id);
                        return (
                          <div
                            key={emp.id}
                            className="flex items-center gap-3 p-3 hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={isAssigned}
                              onChange={() => toggleEmpresa(emp.id)}
                              className="h-4 w-4 text-blue-600 rounded cursor-pointer"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{emp.nombre}</p>
                              <p className="text-xs text-gray-500">{emp.identificacion}</p>
                            </div>
                            {isAssigned && (
                              <select
                                value={formData.empresas.find(e => e.emisor_id === emp.id)?.rol_id || ''}
                                onChange={(e) => {
                                  const rolId = e.target.value ? parseInt(e.target.value) : null;
                                  setFormData(prev => ({
                                    ...prev,
                                    empresas: prev.empresas.map(em =>
                                      em.emisor_id === emp.id ? { ...em, rol_id: rolId } : em
                                    )
                                  }));
                                }}
                                className="text-xs border border-gray-300 rounded px-2 py-1 w-36"
                                title="Rol en esta empresa (vacío = rol global)"
                              >
                                <option value="">Rol global</option>
                                {roles.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                              </select>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Provider Restrictions Section */}
                {user && hasCxPPermission && formData.empresas.length > 0 && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <UserCog size={18} className="text-gray-500" />
                        <label className="text-sm font-medium text-gray-700">
                          Restricción de Proveedores
                        </label>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowProveedoresModal(true)}
                        className="px-3 py-1.5 text-sm border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 bg-white"
                      >
                        Configurar
                      </button>
                    </div>

                    {hasRestrictions ? (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                        <p className="text-xs text-amber-800">
                          <strong>Restringido:</strong> {proveedoresConfig.grupos.length} grupo(s), {proveedoresConfig.proveedores.length} proveedor(es)
                        </p>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                        <p className="text-xs text-blue-800">
                          Sin restricciones - ve todos los proveedores
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-6 mt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 hover:border-green-600 disabled:opacity-50 flex items-center gap-2 transition-colors"
              >
                {loading ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Guardar
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Provider Assignment Modal */}
      {showProveedoresModal && (
        <ProveedoresPermitidosModal
          userId={user?.id}
          emisorId={formData.empresas[0]?.emisor_id}
          config={proveedoresConfig}
          onClose={() => setShowProveedoresModal(false)}
          onSave={handleSaveProveedoresConfig}
        />
      )}
    </>
  );
}

// Provider Assignment Modal Component
function ProveedoresPermitidosModal({ userId, emisorId, config, onClose, onSave }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [searchProv, setSearchProv] = useState('');

  const [selectedGrupos, setSelectedGrupos] = useState(config?.grupos || []);
  const [selectedProveedores, setSelectedProveedores] = useState(config?.proveedores || []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [gruposRes, proveedoresRes] = await Promise.all([
        gastosApi.listGruposProveedores(),
        gastosApi.listProveedores({ limit: 500 })
      ]);
      setGrupos(gruposRes.data.data || []);
      setProveedores(proveedoresRes.data.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const toggleGrupo = (grupoId) => {
    if (selectedGrupos.includes(grupoId)) {
      setSelectedGrupos(selectedGrupos.filter(id => id !== grupoId));
    } else {
      setSelectedGrupos([...selectedGrupos, grupoId]);
    }
  };

  const toggleProveedor = (proveedorId) => {
    if (selectedProveedores.includes(proveedorId)) {
      setSelectedProveedores(selectedProveedores.filter(id => id !== proveedorId));
    } else {
      setSelectedProveedores([...selectedProveedores, proveedorId]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      onSave({
        grupos: selectedGrupos,
        proveedores: selectedProveedores
      });
    } finally {
      setSaving(false);
    }
  };

  const filteredProveedores = proveedores.filter(p =>
    searchProv === '' ||
    p.nombre.toLowerCase().includes(searchProv.toLowerCase()) ||
    p.identificacion?.includes(searchProv)
  );

  const hasRestrictions = selectedGrupos.length > 0 || selectedProveedores.length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-xl w-full max-w-4xl mx-4 shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h3 className="text-lg font-semibold">Asignar Proveedores Permitidos</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Info Message */}
        {!hasRestrictions && !loading && (
          <div className="mx-6 mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle size={20} className="text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-blue-800 font-medium">Sin restricciones asignadas</p>
              <p className="text-sm text-blue-700">
                El usuario puede ver todas las facturas de todos los proveedores.
                Seleccione grupos o proveedores individuales para restringir el acceso.
              </p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-2 text-gray-500">Cargando...</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              {/* Groups Column */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Grupos de Proveedores
                </h4>
                <div className="border rounded-lg max-h-80 overflow-y-auto">
                  {grupos.length === 0 ? (
                    <p className="p-4 text-gray-500 text-sm text-center">
                      No hay grupos creados.
                    </p>
                  ) : (
                    <div className="divide-y">
                      {grupos.map((grupo) => (
                        <label
                          key={grupo.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedGrupos.includes(grupo.id)}
                            onChange={() => toggleGrupo(grupo.id)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300"
                          />
                          <div
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: grupo.color || '#6B7280' }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{grupo.nombre}</p>
                            <p className="text-xs text-gray-500">{grupo.cantidad_proveedores || 0} proveedores</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Individual Providers Column */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Proveedores Individuales
                </h4>

                {/* Search */}
                <div className="relative mb-3">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar proveedor..."
                    value={searchProv}
                    onChange={(e) => setSearchProv(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {filteredProveedores.length === 0 ? (
                    <p className="p-4 text-gray-500 text-sm text-center">
                      {searchProv ? 'No se encontraron proveedores' : 'No hay proveedores registrados'}
                    </p>
                  ) : (
                    <div className="divide-y">
                      {filteredProveedores.map((prov) => (
                        <label
                          key={prov.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedProveedores.includes(prov.id)}
                            onChange={() => toggleProveedor(prov.id)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{prov.nombre}</p>
                            <p className="text-xs text-gray-500">{prov.identificacion}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 hover:border-green-600 disabled:opacity-50 flex items-center gap-2 transition-colors"
          >
            {saving ? (
              <>
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                Guardando...
              </>
            ) : (
              <>
                <Check size={18} />
                Guardar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
